import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import HierarchicalCheckbox from './HierarchicalCheckbox';
import { fetchHHSpecializations, type SpecializationOption } from '../../services/hhDictionariesService';

interface HierarchicalItem {
  id: string;
  label: string;
  count?: number;
  children?: HierarchicalItem[];
}

interface SpecializationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSpecializations: string[];
  onSelectionChange: (specializations: string[]) => void;
  selectionMode?: 'multi' | 'single_category';
  title?: string;
  subtitle?: string;
  /**
   * Per-source загрузчик справочника. Если передан — используется вместо
   * дефолтного fetchHHSpecializations. Для источников без подключённого
   * словаря (например avito пока) сюда передаётся либо undefined → fallback
   * на пустой массив, либо конкретная fetch-функция этого источника.
   */
  fetchData?: () => Promise<SpecializationOption[]>;
  /**
   * Режим рендера справочника:
   *   - 'hierarchical' (по умолчанию) — дерево с path-ID (для HH)
   *   - 'flat'                        — плоский список с поиском (для Avito)
   */
  mode?: 'flat' | 'hierarchical';
}

const SpecializationModal: React.FC<SpecializationModalProps> = ({
  isOpen,
  onClose,
  selectedSpecializations,
  onSelectionChange,
  selectionMode = 'multi',
  title = 'Специализации',
  subtitle,
  fetchData,
  mode = 'hierarchical',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [specializations, setSpecializations] = useState<SpecializationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSpecializations = async () => {
      if (!isOpen) {
        return;
      }
      // Обратная совместимость: исторически SpecializationModal вызывался
      // в нескольких местах без fetchData (HH-only — search header, vacancy
      // edit form и т.п.) — там данные жёстко берутся у HH. Чтобы не ломать
      // эти места, при отсутствии fetchData падаем на fetchHHSpecializations.
      // Source с не подключённым словарём (например avito пока) должен явно
      // передать fetchData: async () => [] из своего sourceConfig.
      const loader = fetchData ?? fetchHHSpecializations;
      setLoading(true);
      setError(null);
      try {
        const data = await loader();
        setSpecializations(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Не удалось получить список специализаций');
        setSpecializations([]);
      } finally {
        setLoading(false);
      }
    };

    void loadSpecializations();
  }, [isOpen, fetchData]);

  const pathKeyToOrigIdRef = useRef(new Map<string, string>());
  const origIdToPathKeysRef = useRef(new Map<string, string[]>());
  
  const specializationData = useMemo(() => {
    if (specializations.length === 0) return [];
    
    pathKeyToOrigIdRef.current.clear();
    origIdToPathKeysRef.current.clear();
    
    const convert = (items: SpecializationOption[], prefix = ''): HierarchicalItem[] => {
      return items.map(item => {
        const pathId = prefix ? `${prefix}/${item.id}` : item.id;
        pathKeyToOrigIdRef.current.set(pathId, item.id);
        const list = origIdToPathKeysRef.current.get(item.id) || [];
        list.push(pathId);
        origIdToPathKeysRef.current.set(item.id, list);
        return {
          id: pathId,
          label: item.name,
          children: item.roles ? convert(item.roles, pathId) : undefined
        } as HierarchicalItem;
      });
    };
    
    return convert(specializations);
  }, [specializations]);

  const getAllChildrenIds = (item: HierarchicalItem): string[] => {
    const result: string[] = [];
    if (item.children) {
      for (const child of item.children) {
        result.push(child.id);
        result.push(...getAllChildrenIds(child));
      }
    }
    return result;
  };

  const buildParentIndex = (items: HierarchicalItem[], parent: string | null, map: Record<string, string | null>) => {
    for (const it of items) {
      map[it.id] = parent;
      if (it.children && it.children.length > 0) buildParentIndex(it.children, it.id, map);
    }
  };

  const parentIndex = useMemo(() => {
    const index: Record<string, string | null> = {};
    buildParentIndex(specializationData, null, index);
    return index;
  }, [specializationData]);

  const getAncestors = (id: string): string[] => {
    const result: string[] = [];
    let current: string | null = parentIndex[id] ?? null;
    while (current) {
      result.push(current);
      current = parentIndex[current] ?? null;
    }
    return result;
  };

  const getAllLeafIds = (item: HierarchicalItem): string[] => {
    if (!item.children || item.children.length === 0) return [item.id];
    let res: string[] = [];
    for (const child of item.children) res = res.concat(getAllLeafIds(child));
    return res;
  };

  const [selectedPathKeys, setSelectedPathKeys] = useState<string[]>([]);
  const [localSelectedPathKeys, setLocalSelectedPathKeys] = useState<string[]>([]);

  const leafPathKeys = useMemo(() => {
    const set = new Set<string>();
    const walk = (items: HierarchicalItem[]) => {
      for (const item of items) {
        if (!item.children || item.children.length === 0) {
          set.add(item.id);
        } else {
          walk(item.children);
        }
      }
    };
    walk(specializationData);
    return set;
  }, [specializationData]);

  const compressSelection = (selected: Set<string>): string[] => {
    const walk = (node: HierarchicalItem): { ids: string[]; covered: boolean } => {
      if (selected.has(node.id)) return { ids: [node.id], covered: true };
      if (!node.children || node.children.length === 0) return { ids: [], covered: selected.has(node.id) };
      let allCovered = true;
      let collected: string[] = [];
      for (const child of node.children) {
        const res = walk(child);
        if (!res.covered) allCovered = false;
        collected = collected.concat(res.ids);
      }
      if (collected.length === 0) return { ids: [], covered: false };
      if (allCovered) return { ids: [node.id], covered: true };
      return { ids: collected, covered: false };
    };
    const result: string[] = [];
    for (const root of specializationData) {
      const res = walk(root);
      result.push(...res.ids);
    }
    return Array.from(new Set(result));
  };

  useEffect(() => {
    if (!isOpen || specializationData.length === 0) return;
    const init: string[] = [];
    const knownKeys = new Set<string>(Object.keys(parentIndex));

    const pickBestPathKey = (raw: string): string | null => {
      if (knownKeys.has(raw)) return raw;
      const paths = origIdToPathKeysRef.current.get(raw) || [];
      if (paths.length === 0) return null;
      const leafPaths = paths.filter((p) => leafPathKeys.has(p));
      const candidates = (leafPaths.length > 0 ? leafPaths : paths);
      const preferred = [...candidates].sort((a, b) => b.split('/').length - a.split('/').length)[0];
      return preferred || null;
    };

    for (const orig of selectedSpecializations) {
      const raw = String(orig);
      const preferred = pickBestPathKey(raw);
      if (preferred) init.push(preferred);
    }

    // В режиме single_category раскрываем группы до листовых элементов,
    // чтобы выбранная группа отображалась как N листьев (а не 1 элемент)
    if (selectionMode === 'single_category') {
      const expanded: string[] = [];
      for (const key of init) {
        if (leafPathKeys.has(key)) {
          expanded.push(key);
        } else {
          const node = findItemById(specializationData, key);
          if (node) {
            expanded.push(...getAllLeafIds(node));
          }
        }
      }
      setSelectedPathKeys(expanded);
      setLocalSelectedPathKeys(expanded);
    } else {
      setSelectedPathKeys(init);
      setLocalSelectedPathKeys(init);
    }
  }, [isOpen, selectedSpecializations, specializationData, selectionMode]);

  const findItemById = (items: HierarchicalItem[], id: string): HierarchicalItem | null => {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  const handleToggle = (id: string, checked: boolean) => {
    const item = findItemById(specializationData, id);
    if (!item) return;

    if (selectionMode === 'single_category') {
      // Разрешаем выбирать только листовые специализации (не группы)
      if (!leafPathKeys.has(id)) return;
      // Клик на уже единственный выбранный — снять выбор
      if (localSelectedPathKeys.length === 1 && localSelectedPathKeys[0] === id) {
        setLocalSelectedPathKeys([]);
      } else {
        setLocalSelectedPathKeys([id]);
      }
      return;
    }

    const set = new Set<string>(localSelectedPathKeys);
    if (checked) {
      set.add(id);
    } else {
      set.delete(id);
      const ancestors = getAncestors(id);
      const ancestorWithSelection = ancestors.find(aid => set.has(aid));
      if (ancestorWithSelection) {
        set.delete(ancestorWithSelection);
        const ancestorItem = findItemById(specializationData, ancestorWithSelection);
        if (ancestorItem) {
          const allLeafsArr = getAllLeafIds(ancestorItem);
          const removedLeafsSet = new Set(getAllLeafIds(item));
          for (let i = 0; i < allLeafsArr.length; i++) {
            const leaf = allLeafsArr[i];
            if (!removedLeafsSet.has(leaf)) set.add(leaf);
          }
        }
      } else if (item.children && item.children.length > 0) {
        for (const cid of getAllChildrenIds(item)) set.delete(cid);
      }
    }
    const normalized = compressSelection(set);
    setLocalSelectedPathKeys(normalized);
  };

  const handleToggleExpanded = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleReset = () => {
    setLocalSelectedPathKeys([]);
  };

  const handleSave = () => {
    onSelectionChange(localSelectedPathKeys);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelectedPathKeys(selectedPathKeys);
    onClose();
  };

  const filterData = (items: HierarchicalItem[], searchTerm: string): HierarchicalItem[] => {
    if (!searchTerm) {
      return items;
    }

    const search = searchTerm.toLowerCase();
    return items.reduce<HierarchicalItem[]>((acc, item) => {
      const matchesSearch = item.label.toLowerCase().includes(search);
      const filteredChildren = item.children ? filterData(item.children, searchTerm) : undefined;

      if (matchesSearch || (filteredChildren && filteredChildren.length > 0)) {
        acc.push({
          ...item,
          children: filteredChildren
        });
      }

      return acc;
    }, []);
  };

  const filteredData = filterData(specializationData, searchTerm);
  const expandForDisplay = (selected: string[]): string[] => {
    const s = new Set<string>();
    for (const sel of selected) {
      s.add(sel);
      const node = findItemById(specializationData, sel);
      if (node) for (const cid of getAllChildrenIds(node)) s.add(cid);
    }
    return Array.from(s);
  };
  const checkedForDisplay = expandForDisplay(localSelectedPathKeys);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" data-testid="modal-overlay">
      <div className="modal-container flex flex-col" data-testid="modal-container" data-modal="specialization">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-amber-600 mt-1">{subtitle}</p>}
          </div>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pb-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e16349] focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">Загрузка...</div>
            </div>
          )}
          
          {error && (
            <div className="flex justify-center items-center py-8">
              <div className="text-red-500">{error}</div>
            </div>
          )}
          
          {!loading && !error && filteredData.length === 0 && (
            <div className="flex justify-center items-center py-8">
              <div className="text-gray-500">Ничего не найдено</div>
            </div>
          )}
          
          {!loading && !error && filteredData.length > 0 && mode === 'flat' && (
            // Плоский режим (Avito specialization и т.п.): один уровень,
            // простые чекбоксы, без раскрытия. Поиск/выделение работает
            // через тот же checkedForDisplay/handleToggle что и иерархия.
            <div className="space-y-1">
              {filteredData.map((item) => {
                const isChecked = checkedForDisplay.includes(item.id);
                return (
                  <label
                    key={item.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-current"
                      checked={isChecked}
                      onChange={(e) => handleToggle(item.id, e.target.checked)}
                    />
                    <span className="text-sm text-gray-800">{item.label}</span>
                  </label>
                );
              })}
            </div>
          )}

          {!loading && !error && filteredData.length > 0 && mode !== 'flat' && (
            <div className="space-y-1">
              {filteredData.map((item) => (
                <HierarchicalCheckbox
                  key={item.id}
                  item={item}
                  checkedItems={checkedForDisplay}
                  onToggle={handleToggle}
                  expandedItems={expandedItems}
                  onToggleExpanded={handleToggleExpanded}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 mt-auto">
          <button
            onClick={handleReset}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            Сбросить
          </button>
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Отменить
            </button>
            <button
              onClick={handleSave}
              disabled={selectionMode === 'single_category' ? localSelectedPathKeys.length !== 1 : false}
              className={`px-4 py-2 rounded-lg transition-colors ${
                (selectionMode === 'single_category' ? localSelectedPathKeys.length !== 1 : false)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#e16349] text-white hover:bg-[#d14d3a]'
              }`}
            >
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SpecializationModal;
